import React, { useState } from 'react';
import { Mail, Copy, Check } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import Modal from '../../common/modals/Modal';
import { useAit } from '../../../context/AitContext';
import toast from 'react-hot-toast';

const AitInviteMemberModal = ({ isOpen, onClose, propertyId, onInviteSent }) => {
  const { theme } = useTheme();
  const { invitePropertyMember } = useAit();
  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleInviteByEmail = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setLoading(true);
    const result = await invitePropertyMember(propertyId, { email: email.trim(), role });
    if (result.success) {
      toast.success(`Invitation sent to ${email}`);
      setEmail('');
      onInviteSent?.();
      onClose();
    } else {
      toast.error(result.error || 'Failed to send invitation');
    }
    setLoading(false);
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    const result = await invitePropertyMember(propertyId, { invite_type: 'link', role });
    if (result.success) {
      setInviteUrl(result.invitation.invite_url);
      toast.success('Invite link generated');
    } else {
      toast.error(result.error || 'Failed to generate link');
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setMethod('email');
    setEmail('');
    setRole('viewer');
    setInviteUrl('');
    setCopied(false);
    onClose();
  };

  const isDark = theme === 'dark';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite to Property" size="lg">
      {/* Method Tabs */}
      <div className={`flex gap-4 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => setMethod('email')}
          className={`pb-3 px-1 text-sm transition-colors ${
            method === 'email'
              ? isDark ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600'
              : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Email Invitation
        </button>
        <button
          onClick={() => setMethod('link')}
          className={`pb-3 px-1 text-sm transition-colors ${
            method === 'link'
              ? isDark ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600'
              : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Copy className="w-4 h-4 inline mr-2" />
          Copy Link
        </button>
      </div>

      {/* Email Method */}
      {method === 'email' && (
        <div>
          <label className={`block mb-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="colleague@example.com"
            disabled={loading}
          />

          <label className={`block mt-4 mb-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Role
          </label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} disabled={loading}>
            <option value="viewer">Viewer (Read-only)</option>
            <option value="editor">Editor (Can edit + invite)</option>
          </select>

          <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            They'll need a Jpro account with this email to accept the invitation.
          </p>

          <button
            onClick={handleInviteByEmail}
            disabled={loading || !email.trim()}
            className={`w-full mt-6 py-2 rounded-lg font-semibold text-sm transition-colors ${
              loading || !email.trim()
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      )}

      {/* Link Method */}
      {method === 'link' && (
        <div>
          <label className={`block mb-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={inputCls}
            disabled={loading || !!inviteUrl}
          >
            <option value="viewer">Viewer (Read-only)</option>
            <option value="editor">Editor (Can edit + invite)</option>
          </select>

          {!inviteUrl ? (
            <>
              <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Generate a link that anyone with a Jpro account can use to join as a {role}.
              </p>
              <button
                onClick={handleGenerateLink}
                disabled={loading}
                className={`w-full mt-6 py-2 rounded-lg font-semibold text-sm transition-colors ${
                  loading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {loading ? 'Generating...' : 'Generate Invite Link'}
              </button>
            </>
          ) : (
            <>
              <div className={`mt-4 p-3 rounded-lg font-mono text-sm break-all ${
                isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}>
                {inviteUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="w-full mt-4 py-2 rounded-lg font-semibold text-sm bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 transition-colors"
              >
                {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
              </button>
              <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Anyone with this link can join as a <strong>{role}</strong>. Link expires in 7 days.
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AitInviteMemberModal;
